/**
 * ── THE 12-MONTH WARRANTY CLOCK, BROWSER SIDE ─────────────────────────────────────────────────
 *
 * THE TWIN of `apps/api/src/app/rma/warranty.ts`, duplicated rather than imported because
 * apps/api does not depend on this package — the same arrangement `opticlock.format.ts` documents
 * and that `rmaFormFor` / the vendored `FORM_MODEL` already live under. `WARRANTY_CASES` below is
 * the drift detector: both specs assert against this one table, so the two copies cannot disagree
 * without a test going red.
 *
 * WHY THE BROWSER NEEDS IT AT ALL (requester, 2026-09-22: "once I click look up in Shopify, it
 * should look for the warranty"). The server derives the verdict from the SAVED purchase date,
 * which is the right source for the queue and the drawer — but in the dialog the verifier has just
 * pulled a date out of Shopify and has not saved anything yet. Making them press Confirm to find
 * out whether the device is covered inverts the order of the decision: the answer is what they
 * press Confirm *because of*.
 *
 * The rule itself, from the customer-facing KB (`rma-warranty-process`): a 13-month manufacturer
 * warranty running from the purchase date, and a separate 30-day window for returning an unused
 * device. Date-only arithmetic throughout — a purchase is a calendar day, and the answer must not
 * depend on the hour a browser happens to be in.
 */

export const WARRANTY_MONTHS = 13;
export const RETURN_WINDOW_DAYS = 30;

export type RmaWarrantyState = 'IN_WARRANTY' | 'EXPIRED' | 'UNKNOWN';

export interface RmaWarrantyAssessment {
  state: RmaWarrantyState;
  endsOn?: string;
  daysOwned?: number;
  daysRemaining?: number;
  withinReturnWindow?: boolean;
}

export function parseOrderDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return dt;
}

export function toDateString(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Adds months, clamping a short month: 31 Jan + 1 is 28/29 Feb, never 2/3 March. */
export function addMonths(d: Date, months: number): Date {
  const day = d.getUTCDate();
  const shifted = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1));
  const lastOfMonth = new Date(Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 0)).getUTCDate();
  shifted.setUTCDate(Math.min(day, lastOfMonth));
  return shifted;
}

const MS_DAY = 86_400_000;

/** Where a purchase sits against both clocks. UNKNOWN for missing, unreadable or future dates. */
export function assessWarranty(
  orderDate: string | Date | null | undefined,
  now: Date = new Date(),
): RmaWarrantyAssessment {
  const start = parseOrderDate(orderDate);
  if (!start) return { state: 'UNKNOWN' };
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const daysOwned = Math.round((today.getTime() - start.getTime()) / MS_DAY);
  if (daysOwned < 0) return { state: 'UNKNOWN' };
  const endsAt = addMonths(start, WARRANTY_MONTHS);
  const daysRemaining = Math.round((endsAt.getTime() - today.getTime()) / MS_DAY);
  return {
    state: daysRemaining >= 0 ? 'IN_WARRANTY' : 'EXPIRED',
    endsOn: toDateString(endsAt),
    daysOwned,
    daysRemaining,
    withinReturnWindow: daysOwned <= RETURN_WINDOW_DAYS,
  };
}

/**
 * THE DRIFT DETECTOR. Both copies of this rule — this one and apps/api's — are asserted against
 * exactly these cases, so the two cannot drift without a spec failing. Edit one implementation and
 * its own spec fails; edit this table and you are told to go and check the other side too.
 *
 * Every row is a boundary somebody could get wrong: the anniversary itself, the day after, the
 * return-window edge, a purchase whose end month is shorter, and the future date that means a typo'd year.
 */
export const WARRANTY_CASES: ReadonlyArray<{
  bought: string;
  now: string;
  state: RmaWarrantyState;
  endsOn?: string;
  daysRemaining?: number;
  withinReturnWindow?: boolean;
}> = [
  { bought: '2026-09-22', now: '2026-09-22', state: 'IN_WARRANTY', endsOn: '2027-10-22', daysRemaining: 395, withinReturnWindow: true },
  { bought: '2025-08-22', now: '2026-09-22', state: 'IN_WARRANTY', endsOn: '2026-09-22', daysRemaining: 0, withinReturnWindow: false },
  { bought: '2025-08-21', now: '2026-09-22', state: 'EXPIRED', endsOn: '2026-09-21', daysRemaining: -1, withinReturnWindow: false },
  { bought: '2026-08-23', now: '2026-09-22', state: 'IN_WARRANTY', endsOn: '2027-09-23', daysRemaining: 366, withinReturnWindow: true },
  { bought: '2026-08-22', now: '2026-09-22', state: 'IN_WARRANTY', endsOn: '2027-09-22', daysRemaining: 365, withinReturnWindow: false },
  { bought: '2026-01-31', now: '2026-02-01', state: 'IN_WARRANTY', endsOn: '2027-02-28', daysRemaining: 392, withinReturnWindow: true },
  { bought: '2027-01-01', now: '2026-09-22', state: 'UNKNOWN' },
];
